import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { integrityStyles } from "./integrityTheme";
import { findOffer, pillars, offersByPillar, CONTACT_EMAIL } from "./offers";
import { setMeta, setJsonLd, SITE_URL, breadcrumbJsonLd, faqJsonLd } from "./seo";
import ContactForm from "./ContactForm";
import SiteFooter from "./SiteFooter";

export default function ServicePage() {
  const { slug } = useParams();
  const offer = findOffer(slug);
  const pillar = offer && pillars.find((p) => p.key === offer.pillar);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!offer) return undefined;
    const url = `${SITE_URL}/services/${offer.slug}`;
    setMeta({
      title: `${offer.name} — Documore Integrity`,
      description: `${offer.summary} ${offer.lede}`.slice(0, 300),
      url,
    });
    const cleanups = [
      setJsonLd("service", {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${url}#service`,
        name: offer.name,
        serviceType: offer.name,
        description: offer.lede,
        url,
        category: pillar.name,
        provider: { "@id": `${SITE_URL}/#organization`, "@type": "Organization", name: "Documore", url: SITE_URL },
        areaServed: "US",
      }),
      setJsonLd("breadcrumb", breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name: offer.name, path: `/services/${offer.slug}` },
      ])),
      offer.faqs && offer.faqs.length ? setJsonLd("faq", faqJsonLd(offer.faqs)) : null,
    ];
    return () => cleanups.forEach((c) => c && c());
  }, [offer, pillar]);

  if (!offer) {
    return (
      <>
        <style>{integrityStyles}</style>
        <div className="di-root">
          <div className="di-wrap di-section">
            <h1 className="di-h2">Service not found</h1>
            <p className="di-text" style={{ marginTop: 12 }}>
              <Link to="/services">See all services</Link>
            </p>
          </div>
        </div>
      </>
    );
  }

  const related = offersByPillar(offer.pillar).filter((o) => o.slug !== offer.slug);

  return (
    <>
      <style>{integrityStyles}</style>
      <div className="di-root">
        <div className="di-wrap">
          {/* Hero */}
          <div className="di-hero">
            <div>
              <div className="di-kicker">
                <span className="di-kicker-line" />
                <span className="di-kicker-text">
                  <Link to={`/services#${pillar.key}`}>{pillar.name}</Link> · {offer.name}
                </span>
              </div>
              <h1 className="di-h1">{offer.headline}</h1>
            </div>
            <div style={{ display: "grid", gap: 24 }}>
              <p className="di-lede">{offer.lede}</p>
              <div style={{ paddingLeft: 22 }}>
                <a className="di-btn" href="#start">Request an assessment</a>
              </div>
            </div>
          </div>

          {/* Risk / deliverables */}
          <section className="di-section" style={{ paddingTop: 16 }}>
            <div className="di-grid-2">
              <div className="di-cell">
                <h2 className="di-h3" style={{ marginBottom: 16 }}>The risk</h2>
                <ul className="di-list">{offer.risk.map((t) => <li key={t}>{t}</li>)}</ul>
              </div>
              <div className="di-cell">
                <h2 className="di-h3" style={{ marginBottom: 16 }}>Deliverables</h2>
                <ul className="di-list">{offer.deliverables.map((t) => <li key={t}>{t}</li>)}</ul>
              </div>
            </div>
          </section>

          {/* Inputs / method */}
          <section className="di-section" style={{ paddingTop: 0 }}>
            <div className="di-grid-2">
              <div className="di-cell">
                <h2 className="di-h3" style={{ marginBottom: 16 }}>What we require</h2>
                <ul className="di-list">{offer.inputs.map((t) => <li key={t}>{t}</li>)}</ul>
              </div>
              <div className="di-cell">
                <h2 className="di-h3" style={{ marginBottom: 16 }}>Method</h2>
                <ol className="di-steps">{offer.method.map((t) => <li key={t}>{t}</li>)}</ol>
              </div>
            </div>
          </section>

          {/* Engagement standard */}
          <section className="di-section" style={{ paddingTop: 0 }}>
            <h2 className="di-h2" style={{ marginBottom: 20 }}>Engagement standard</h2>
            <dl className="di-facts">
              <dt>Scope</dt>
              <dd>
                Inputs, deliverables and acceptance criteria are defined in a written statement of work before any
                work begins. Results are assessed only against that standard.
              </dd>
              <dt>Timeline</dt>
              <dd>{offer.timeline}</dd>
              {offer.scopeNote && (
                <>
                  <dt>Boundaries</dt>
                  <dd>{offer.scopeNote}</dd>
                </>
              )}
              <dt>Evidence</dt>
              <dd>Every finding is traceable to its source. Nothing is guessed or silently corrected.</dd>
              <dt>Confidentiality</dt>
              <dd>
                Client data is used only for the engagement, never to train models or for other clients, and is
                deleted within 30 days of acceptance. <Link to="/privacy">Privacy</Link>
              </dd>
            </dl>
          </section>

          {/* FAQ */}
          {offer.faqs && offer.faqs.length > 0 && (
            <section className="di-section" style={{ paddingTop: 0 }}>
              <h2 className="di-h2" style={{ marginBottom: 20 }}>Questions</h2>
              <div className="di-faq">
                {offer.faqs.map((f) => (
                  <div key={f.q} className="di-faq-item">
                    <h3 className="di-faq-q">{f.q}</h3>
                    <p className="di-text">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Start */}
          <section id="start" className="di-section" style={{ paddingTop: 16 }}>
            <hr className="di-rule" />
            <div className="di-hero" style={{ alignItems: "start", paddingBottom: 0 }}>
              <div style={{ display: "grid", gap: 16 }}>
                <h2 className="di-h2">Request an assessment</h2>
                <p className="di-text">
                  Describe the system and the decision it supports. We respond within one business day with a
                  proposed scope and statement of work. Or write to{" "}
                  <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(offer.name)}`}>{CONTACT_EMAIL}</a>.
                </p>
              </div>
              <ContactForm service={offer.name} placeholder="The system, the data involved, and the decision it supports" />
            </div>
          </section>

          {/* Related */}
          {related.length > 0 && (
            <section className="di-section">
              <hr className="di-rule" />
              <div className="di-kicker" style={{ marginTop: 40 }}>
                <span className="di-kicker-line" />
                <span className="di-kicker-text">More {pillar.name}</span>
              </div>
              <div className="di-grid-2">
                {related.map((o) => (
                  <Link key={o.slug} to={`/services/${o.slug}`} className="di-card">
                    <span className="di-h3">{o.name}</span>
                    <span className="di-text" style={{ fontSize: 14 }}>{o.summary}</span>
                    <span className="di-card-more">View capability →</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
        <SiteFooter />
      </div>
    </>
  );
}
